import React, { useState } from 'react';
import s from './s.module.less';
import dayjs from 'dayjs';
import { Button, Modal, Space, Typography, Calendar } from 'antd';
import arrowRightIcon from 'assets/common/arrowRightIcon.svg';
import doubleRightIcon from 'assets/common/doubleRIght.svg';
import type { Dayjs } from 'dayjs';

const { Text } = Typography;

type Props = {
    open: boolean;
    onCancel: () => void;
    onConfirm: (date: dayjs.Dayjs) => void;
    defaultTime?: number,
    confirmLoading?: boolean,
    disabledDate?: (date: Dayjs) => boolean;
};

export default function DateSelect({ open, onCancel, onConfirm, defaultTime, confirmLoading, disabledDate }: Props) {
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
    const [timeZone, setTimeZone] = React.useState<string>('');

    React.useEffect(() => {
        if (open) {
            const options = { timeZoneName: 'long' };
            let _timeZone = '';
            try {
                _timeZone = `Timezone : ${Intl.DateTimeFormat(undefined, options as any).resolvedOptions().timeZone}`;
                const offsetMinutes = new Date().getTimezoneOffset();
                const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
                const offsetPrefix = offsetMinutes < 0 ? '+' : '-';
                const offsetString = `${offsetPrefix}${offsetHours
                    .toString()
                    .padStart(2, '0')}:00`;
                _timeZone = `${_timeZone} ${offsetString}`;
                setTimeZone(_timeZone);
            } catch (e) {
                console.error(e);
            }
            if (!selectedDate) {
                if (defaultTime) {
                    setSelectedDate(dayjs(defaultTime));
                } else {
                    setSelectedDate(dayjs().add(7, 'day'));
                }
            }
        }
    }, [defaultTime, open, selectedDate]);
    const handleConfirm = () => {
        if (selectedDate) {
            onConfirm(selectedDate);
        }
    };

    const handleCancel = () => {
        setSelectedDate(null);
        onCancel();
    };

    const dateFullCellRender = (current: Dayjs) => {
        const isSelected = selectedDate?.isSame(current, 'day');

        return (
            <div
                style={{
                    // background: isSelected ? '#00816B' : 'transparent',
                    color: isSelected ? '#111827' : 'inherit',
                    borderColor: isSelected ? '#111827' : 'transparent',
                }}
                className={s.dateCell}
                onClick={(e) => {
                    setSelectedDate(current);
                    e.stopPropagation();
                }}
            >
                {current.date()}
            </div>
        );
    };

    return (
        <Modal
            title="Select expected go-live date"
            open={open}
            onCancel={handleCancel}
            width={480}
            destroyOnClose
            centered
            footer={
                <div className={s.footer}>
                    <Space size={12}>
                        <Button onClick={handleCancel} className={s.cancelBtn}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleConfirm}
                            disabled={!selectedDate}
                            loading={confirmLoading}
                            className={s.confirmBtn}
                        >
                            Confirm
                        </Button>
                    </Space>
                </div>
            }
            className={s.dateSelectModal}
        >
            <div className={s.wrap}>
                {
                    timeZone &&
                    <div className={s.timezoneInfo}>
                        <Text type="secondary" className={s.timezoneText}>
                            Timezone: {timeZone}
                        </Text>
                    </div>
                }

                <div className={s.recommendationAlert}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z" fill="#60A5FA" />
                    </svg>
                    <p>
                        We recommend going live early and avoiding weekends, so you can secure bookings sooner when patients are most active.
                    </p>
                </div>

                <Calendar
                    disabledDate={disabledDate || ((date) => {
                        const now = new Date();
                        now.setHours(0, 0, 0, 0);
                        const todayStart = now.getTime();

                        if (!date) {
                            return false;
                        }
                        // 只禁用严格小于今天的日期，允许选择今天及以后的日期
                        return date.valueOf() < todayStart;
                    })}
                    fullscreen={false}
                    headerRender={({ value: headerValue, onChange }) => (
                        <div className="flex justify-between items-center">
                            <div>
                                <Button type="text" onClick={() => onChange(headerValue.subtract(1, 'year'))}>
                                    <img className="rotate-180" src={doubleRightIcon} />
                                </Button>
                                <Button type="text" onClick={() => onChange(headerValue.subtract(1, 'month'))}>
                                    <img className="scale-110 rotate-180" src={arrowRightIcon} />
                                </Button>
                            </div>
                            <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                                {headerValue.format('MMM YYYY')}
                            </span>
                            <div>
                                <Button type="text" onClick={() => onChange(headerValue.add(1, 'month'))}>
                                    <img className="scale-110" src={arrowRightIcon} />
                                </Button>
                                <Button type="text" onClick={() => onChange(headerValue.add(1, 'year'))}>
                                    <img src={doubleRightIcon} />
                                </Button>
                            </div>
                        </div>
                    )}
                    dateFullCellRender={dateFullCellRender}
                    defaultValue={defaultTime ? dayjs(defaultTime) : undefined}
                />
            </div>
        </Modal>
    );
}
